import { Routes } from '@angular/router';
import { authGuard, noAuthGuard } from '@features/auth';

export const routes: Routes = [
  // HOME
  {
    path: '',
    title: "Bongo's Bingo",
    pathMatch: 'full',
    loadComponent: () => import('@features/home').then((c) => c.HomeComponent),
    data: { page_type: 'home' },
  },
  {
    path: 'privacy-policy',
    title: "Bongo's Bingo - Privacy Policy",
    loadComponent: () => import('@features/home').then((c) => c.PrivacyPolicyComponent),
  },
  {
    path: 'terms-and-conditions',
    title: "Bongo's Bingo - Terms & Conditions",
    loadComponent: () => import('@features/home').then((c) => c.TermsConditionsComponent),
  },
  // IDENTITY
  {
    path: 'register',
    title: "Bongo's Bingo - Register",
    loadComponent: () => import('@features/registration').then((c) => c.RegistrationComponent),
    canActivate: [noAuthGuard],
    data: { page_type: 'register' },
  },
  {
    path: 'activate',
    title: "Bongo's Bingo - Activate",
    loadComponent: () => import('@features/registration').then((c) => c.ActivateComponent),
    canActivate: [noAuthGuard],
  },
  {
    path: 'lock-account',
    title: "Bongo's Bingo - Lock Account",
    loadComponent: () => import('@features/registration').then((c) => c.LockAccountComponent),
  },
  {
    path: 'login',
    title: "Bongo's Bingo - Login",
    loadComponent: () => import('@features/login').then((c) => c.LoginComponent),
    canActivate: [noAuthGuard],
  },
  {
    path: 'reset-password-request',
    title: "Bongo's Bingo - Forgot Password",
    loadComponent: () => import('@features/password').then((c) => c.ResetPasswordRequestComponent),
    canActivate: [noAuthGuard],
  },
  {
    // No guard: a tokened reset link must work regardless of session state (the token is the authorization).
    path: 'forgot-password',
    title: "Bongo's Bingo - Reset Password",
    loadComponent: () => import('@features/password').then((c) => c.ResetPasswordComponent),
  },

  // EVENTS
  {
    path: 'events',
    title: "Bongo's Bingo - Events",
    loadComponent: () => import('@features/events').then((c) => c.EventsComponent),
    data: { page_type: 'event_listing' },
  },
  {
    path: 'events/search',
    title: "Bongo's Bingo - Search Results",
    loadComponent: () => import('@features/events').then((c) => c.SearchResultsComponent),
  },
  {
    // No static `title`: the page owns its own via SeoMetaService (BBS-0055).
    // DefaultTitleStrategy skips setTitle when the route title is undefined;
    // declaring one here overwrites the SSR-rendered title after hydration.
    path: 'events/:locationSlug/:eventSlug',
    loadComponent: () => import('@features/events').then((c) => c.EventDetailsComponent),
    data: { page_type: 'event_detail' },
  },
  {
    path: 'locations',
    title: "Bongo's Bingo - Locations",
    loadComponent: () => import('@features/locations').then((c) => c.LocationsPageComponent),
    data: { page_type: 'location_listing' },
  },
  {
    // No static `title` — see the events/:locationSlug/:eventSlug route (BBS-0055).
    path: 'locations/:locationId',
    loadComponent: () => import('@features/events').then((c) => c.LocationPageComponent),
    data: { page_type: 'location_detail' },
  },
  {
    // No static `title` — see the events/:locationSlug/:eventSlug route (BBS-0055).
    path: 'locations/:locationId/:venueId',
    loadComponent: () => import('@features/events').then((c) => c.VenuePageComponent),
    data: { page_type: 'location_detail' },
  },
  {
    path: 'checkout',
    title: "Bongo's Bingo - Checkout",
    loadComponent: () => import('@features/checkout').then((c) => c.CheckoutPageComponent),
    data: { page_type: 'checkout' },
  },
  {
    path: 'checkout/return',
    canActivate: [authGuard],
    loadComponent: () => import('@features/checkout').then((c) => c.CheckoutReturnPageComponent),
  },
  {
    path: 'checkout/complete/:orderId',
    canActivate: [authGuard],
    title: "Bongo's Bingo - Checkout Complete",
    loadComponent: () => import('@features/checkout').then((c) => c.CheckoutCompletePageComponent),
  },
  {
    path: 'orders/:orderId/checkout',
    canActivate: [authGuard],
    title: "Bongo's Bingo - Order Checkout",
    loadComponent: () => import('@features/checkout').then((c) => c.PrivateCheckoutPageComponent),
  },
  {
    path: 'orders/:orderId/checkout/return',
    canActivate: [authGuard],
    title: "Bongo's Bingo - Order Checkout Return",
    loadComponent: () => import('@features/checkout').then((c) => c.PrivateCheckoutReturnPageComponent),
  },

  // PROFILE
  {
    path: 'profile',
    canActivate: [authGuard],
    children: [
      {
        path: '',
        title: "Bongo's Bingo - Profile",
        loadComponent: () => import('@features/profile').then((c) => c.ProfileComponent),
      },
      // DETAILS
      {
        path: 'details',
        children: [
          {
            path: '',
            title: "Bongo's Bingo - Your Details",
            loadComponent: () => import('@features/profile').then((c) => c.YourDetailsComponent),
          },
          {
            path: 'update-password',
            title: "Bongo's Bingo - Update Password",
            loadComponent: () => import('@features/profile').then((c) => c.UpdatePasswordComponent),
          },
          {
            path: 'update-email/confirm',
            title: "Bongo's Bingo - Update Email",
            loadComponent: () => import('@features/profile').then((c) => c.UpdateEmailConfirmationPageComponent),
          },
          {
            path: 'update-email',
            title: "Bongo's Bingo - Update Email",
            loadComponent: () => import('@features/profile').then((c) => c.UpdateEmailComponent),
          },
        ],
      },
    ],
  },

  // BOOKINGS
  {
    path: 'bookings',
    title: "Bongo's Bingo - Bookings",
    canActivate: [authGuard],
    loadComponent: () => import('@features/bookings').then((c) => c.BookingsComponent),
  },
  {
    path: 'bookings/:id',
    title: "Bongo's Bingo - Assign Tickets",
    canActivate: [authGuard],
    loadComponent: () => import('@features/bookings').then((c) => c.BookingComponent),
  },
  {
    path: 'bookings/:bookingId/tickets/:ticketId',
    title: "Bongo's Bingo - Ticket",
    canActivate: [authGuard],
    loadComponent: () => import('@features/bookings').then((c) => c.TicketComponent),
  },
  // BLOG
  {
    path: 'blog',
    title: "Bongo's Bingo - Blog",
    loadComponent: () => import('@features/custom-pages').then((c) => c.BlogListingComponent),
    data: { section: 'blog', page_type: 'blog_listing' },
  },
  {
    path: 'blog/page/:pageNumber',
    title: "Bongo's Bingo - Blog",
    loadComponent: () => import('@features/custom-pages').then((c) => c.BlogListingComponent),
    data: { section: 'blog', page_type: 'blog_listing' },
  },
  {
    path: 'blog/:slug',
    title: "Bongo's Bingo - Blog",
    loadComponent: () => import('@features/custom-pages').then((c) => c.BlogPostComponent),
    data: { section: 'blog', page_type: 'blog_article' },
  },
  // NEWS
  {
    path: 'news',
    title: "Bongo's Bingo - News",
    loadComponent: () => import('@features/custom-pages').then((c) => c.BlogListingComponent),
    data: { section: 'news', page_type: 'news_listing' },
  },
  {
    path: 'news/page/:pageNumber',
    title: "Bongo's Bingo - News",
    loadComponent: () => import('@features/custom-pages').then((c) => c.BlogListingComponent),
    data: { section: 'news', page_type: 'news_listing' },
  },
  {
    path: 'news/:slug',
    title: "Bongo's Bingo - News",
    loadComponent: () => import('@features/custom-pages').then((c) => c.BlogPostComponent),
    data: { section: 'news', page_type: 'news_article' },
  },
  {
    path: 'pages/:slug',
    loadComponent: () => import('@features/custom-pages').then((c) => c.CustomPageComponent),
    data: { page_type: 'content_page' },
  },
  {
    path: 'contact',
    loadChildren: () => import('@features/contact').then((c) => c.routes),
    data: { page_type: 'contact' },
  },
  {
    path: 'order/:reference',
    redirectTo: '/bookings',
  },
  {
    path: '404',
    title: '404 - Page Not Found',
    loadComponent: () => import('@features/errors').then((c) => c.NotFoundPageComponent),
  },
  {
    path: '**',
    title: '404 - Page Not Found',
    loadComponent: () => import('@features/errors').then((c) => c.NotFoundPageComponent),
  },
];
